import { useMemo, useState } from "react";
import styled from "styled-components";
import DesktopPageHeader from "../../components/DesktopPageHeader";
import { getHeaderTheme } from "../../config/headerTheme";
import { useDeviceType } from "../../hooks/useMediaQuery";
import { useResto } from "../../hooks/useResto";
import { useWebinars } from "../../hooks/useWebinars";
import webinarImage from "../../assets/webinars/almacen.png";
import WebinarModal from "./WebinarModal";
import WebinarSuccessModal from "./WebinarSuccessModal";

const theme = getHeaderTheme();

const monthNames = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const tabs = [
  { id: "upcoming", label: "Próximos eventos" },
  { id: "past", label: "Eventos anteriores" },
];

const formatDate = (dateKey) => {
  if (!dateKey) return "";
  const [year, month, day] = dateKey.split("-").map(Number);
  return `${day} de ${monthNames[month - 1]} de ${year}`;
};

const sortByDate = (direction = "asc") => (first, second) => {
  const firstDate = `${first.fecha || ""} ${first.hora || ""}`;
  const secondDate = `${second.fecha || ""} ${second.hora || ""}`;
  return direction === "asc"
    ? firstDate.localeCompare(secondDate)
    : secondDate.localeCompare(firstDate);
};

const isPastEvent = (webinar, today) =>
  webinar.estado === "finalizado" || (webinar.fecha && webinar.fecha < today);

const WebinarsPage = () => {
  const { usuario, isInscrito, inscribir, setUsuario, webinars } = useWebinars();
  const { resto } = useResto();
  const { isDesktop } = useDeviceType();
  const [activeTab, setActiveTab] = useState("upcoming");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const upcomingEvents = useMemo(
    () => webinars.filter((webinar) => !isPastEvent(webinar, today)).sort(sortByDate("asc")),
    [today, webinars]
  );

  const pastEvents = useMemo(
    () => webinars.filter((webinar) => isPastEvent(webinar, today)).sort(sortByDate("desc")),
    [today, webinars]
  );

  const visibleEvents = activeTab === "upcoming" ? upcomingEvents : pastEvents;

  const handleJoin = (webinar) => {
    if (isPastEvent(webinar, today)) return;
    setSelectedEvent(webinar);
    setIsRegisterModalOpen(true);
  };

  const handleConfirm = ({ nombre, correo }) => {
    if (!selectedEvent) return;
    setUsuario({ nombre, correo });
    inscribir(selectedEvent.id);
    setIsRegisterModalOpen(false);
    setIsSuccessModalOpen(true);
  };

  return (
    <PageContainer>
      {isDesktop && (
        <DesktopPageHeader
          img={resto?.brand || ""}
          alt={resto?.name || ""}
          title={resto?.name || ""}
        />
      )}

      <PageHeader>
        <Title>Comunidad Almacenia</Title>
        <Subtitle>
          Conéctate, aprende y comparte con otros almaceneros en nuestros webinars exclusivos.
        </Subtitle>
      </PageHeader>

      <Tabs role="tablist" aria-label="Vistas de comunidad Almacenia">
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            $active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </TabButton>
        ))}
      </Tabs>

      {visibleEvents.length > 0 ? (
        <CardsGrid>
          {visibleEvents.map((webinar) => (
            <WebinarCard
              key={webinar.id}
              webinar={webinar}
              registered={isInscrito(webinar.id)}
              past={isPastEvent(webinar, today)}
              onJoin={() => handleJoin(webinar)}
            />
          ))}
        </CardsGrid>
      ) : (
        <EmptyState>
          No hay {activeTab === "upcoming" ? "próximos eventos" : "eventos anteriores"} disponibles.
        </EmptyState>
      )}

      <WebinarModal
        isOpen={isRegisterModalOpen}
        webinar={selectedEvent}
        usuario={usuario}
        onClose={() => setIsRegisterModalOpen(false)}
        onConfirm={handleConfirm}
      />
      <WebinarSuccessModal
        isOpen={isSuccessModalOpen}
        webinar={selectedEvent}
        onClose={() => setIsSuccessModalOpen(false)}
      />
    </PageContainer>
  );
};

const WebinarCard = ({ webinar, registered, past, onJoin }) => (
  <Card>
    <CardTop>
      <CardInfo>
        <LiveLabel>
          {!past && <LiveSignalIcon aria-hidden="true" />}
          {past ? "webinar anterior" : "webinar en vivo"}
        </LiveLabel>
        <CardTitle>{webinar.titulo}</CardTitle>
        <MetaGroup>
          <MetaItem>
            <CalendarIcon aria-hidden="true" />
            <span>{formatDate(webinar.fecha)} · {webinar.hora}</span>
          </MetaItem>
          <MetaItem>
            <ClockIcon aria-hidden="true" />
            <span>{webinar.duracion || "45 min"}</span>
          </MetaItem>
        </MetaGroup>
      </CardInfo>
      <ImageWrap>
        <EventImage src={webinarImage} alt="" />
      </ImageWrap>
    </CardTop>

    <Agenda>
      <AgendaTitle>Agenda del webinar</AgendaTitle>
      {(webinar.agenda || []).map((item, index) => (
        <AgendaItem key={`${webinar.id}-${item.hora}-${index}`}>
          <AgendaTime>{item.hora}</AgendaTime>
          <AgendaLine aria-hidden="true">
            <AgendaDot />
          </AgendaLine>
          <AgendaText>{item.texto}</AgendaText>
        </AgendaItem>
      ))}
    </Agenda>

    <JoinButton type="button" onClick={onJoin} disabled={past || registered}>
      {!past && !registered && <CalendarIcon aria-hidden="true" />}
      {past ? "Evento finalizado" : registered ? "Ya estás inscrito" : "Unirme al webinar"}
    </JoinButton>
  </Card>
);

const CalendarIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M7 3v3M17 3v3M4 9h16M6 5h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClockIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LiveSignalIcon = (props) => (
  <svg viewBox="0 0 24 12" fill="none" {...props}>
    <path d="M8 3.5C6.7 4.2 6 5 6 6s.7 1.8 2 2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M5 1.5C3 2.7 2 4.2 2 6s1 3.3 3 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="6" r="2" fill="currentColor" />
    <path d="M16 3.5c1.3.7 2 1.5 2 2.5s-.7 1.8-2 2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M19 1.5c2 1.2 3 2.7 3 4.5s-1 3.3-3 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const PageContainer = styled.div`
  padding: 24px;
  width: 100%;
  min-height: 100%;
  background: #ffffff;
  box-sizing: border-box;

  @media (max-width: 960px) {
    padding: 16px 12px 96px;
  }
`;

const PageHeader = styled.header`
  display: grid;
  gap: 8px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 30px;
  line-height: 1.2;
  color: #111827;

  @media (max-width: 960px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6b7280;
  max-width: 760px;
  line-height: 1.6;
  font-size: 15px;
`;

const Tabs = styled.div`
  display: flex;
  align-items: center;
  gap: 28px;
  width: 100%;
  margin-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 640px) {
    gap: 0;
  }
`;

const TabButton = styled.button`
  position: relative;
  flex: 1;
  border: 0;
  background: transparent;
  color: ${({ $active }) => ($active ? theme.accent : "#6b7280")};
  font-size: 14px;
  font-weight: 800;
  min-height: 42px;
  padding: 0;
  cursor: pointer;
  text-align: center;

  &::after {
    content: "";
    position: absolute;
    right: 0;
    bottom: -1px;
    left: 0;
    height: 3px;
    border-radius: 999px 999px 0 0;
    background: ${({ $active }) => ($active ? theme.accent : "transparent")};
  }

  @media (max-width: 640px) {
    font-size: 13px;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 18px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  border: 1px solid #e6e6e6;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-align: left;
`;

const CardTop = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 118px;
  gap: 16px;
  align-items: start;
  padding: 20px;

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr) 96px;
    gap: 12px;
    padding: 16px;
  }
`;

const CardInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const LiveLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #ffffff;
  background: ${theme.accent};
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  line-height: 1.2;
  margin-bottom: 12px;

  svg {
    width: 20px;
    height: 10px;
    color: #ffffff;
    flex: 0 0 auto;
  }
`;

const CardTitle = styled.h2`
  margin: 0 0 8px;
  color: #111827;
  font-size: 21px;
  line-height: 1.25;
  text-align: left;

  @media (max-width: 640px) {
    font-size: 17px;
  }
`;

const MetaGroup = styled.div`
  display: grid;
  gap: 7px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;

  svg {
    width: 15px;
    height: 15px;
    flex: 0 0 auto;
    color: ${theme.accent};
  }

  @media (max-width: 640px) {
    align-items: flex-start;
    font-size: 12px;
  }
`;

const ImageWrap = styled.div`
  width: 118px;
  height: 118px;
  justify-self: end;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;

  @media (max-width: 640px) {
    width: 96px;
    height: 96px;
  }
`;

const EventImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
`;

const Agenda = styled.div`
  border-top: 1px solid #e5e7eb;
  padding: 18px 20px;
  display: grid;
  gap: 0;
  text-align: left;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const AgendaTitle = styled.h3`
  margin: 0 0 16px;
  color: #111827;
  font-size: 15px;
  font-weight: 800;
  text-align: left;
`;

const AgendaLine = styled.span`
  position: relative;
  display: flex;
  justify-content: center;

  &::after {
    content: "";
    position: absolute;
    top: 12px;
    bottom: -8px;
    width: 2px;
    background: ${theme.accent};
  }
`;

const AgendaItem = styled.div`
  display: grid;
  grid-template-columns: 54px 18px minmax(0, 1fr);
  gap: 12px;
  min-height: 46px;

  &:last-child ${AgendaLine}::after {
    display: none;
  }
`;

const AgendaTime = styled.span`
  color: #374151;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.5;
`;

const AgendaDot = styled.span`
  position: relative;
  width: 10px;
  height: 10px;
  margin-top: 4px;
  border-radius: 999px;
  background: ${theme.accent};
`;

const AgendaText = styled.p`
  margin: 0 0 14px;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.5;
  text-align: left;
`;

const JoinButton = styled.button`
  margin: 0 20px 20px;
  border: 0;
  border-radius: 14px;
  min-height: 48px;
  padding: 0 18px;
  background: ${({ disabled }) => (disabled ? "#d1d5db" : theme.accent)};
  color: #ffffff;
  font-size: 15px;
  font-weight: 800;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: transform 0.15s ease, opacity 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  svg {
    width: 17px;
    height: 17px;
    flex: 0 0 auto;
  }

  &:hover {
    transform: ${({ disabled }) => (disabled ? "none" : "translateY(-1px)")};
    opacity: ${({ disabled }) => (disabled ? 1 : 0.92)};
  }

  @media (max-width: 640px) {
    margin: 0 16px 16px;
  }
`;

const EmptyState = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 28px 20px;
  color: #6b7280;
  font-size: 14px;
  text-align: center;
`;

export default WebinarsPage;
